import { useMemo } from 'react';

import { normalizeClientUrl } from 'lib/jobDocs/normalizeClientUrl';
import { hasValue } from 'lib/jobDocs/hasValue';
import {
  JOB_DOCS_DOCUMENTS_PATH,
  JOB_DOCS_PARAM_PART_NUMBER,
  JOB_DOCS_PARAM_OPERATION_ID,
  JOB_DOCS_PARAM_ACTIVITY_MODE,
  JOB_DOCS_PARAM_HOST_NAV,
  JOB_DOCS_HOST_NAV_INPLACE,
} from 'lib/jobDocs/embedContract';

import { useJobDocsConfig } from './useJobDocsConfig';

// The single route the host addresses in the embedded job-docs-client. The
// `hostNav=inplace` marker tells the embedded app to render opened documents
// in place (over the still-mounted grid) instead of navigating; everything
// past this URL — document routes, view state — is owned by the embed.
const DOCUMENTS_PATH = JOB_DOCS_DOCUMENTS_PATH;

/**
 * @typedef {Object} UseJobDocsIframeResult
 * @property {boolean} showSection True when the Documents section should
 *   render: feature enabled, clientUrl configured, and both `partNumber` and
 *   `operationId` present (numeric `0` is valid).
 * @property {string|null} gridUrl Absolute URL of the document grid — used by
 *   callers as the render gate for the embed. `null` when hidden.
 * @property {string|null} gridPath Pathname + search of {@link gridUrl};
 *   passed as the `path` prop to `MMEmbeddableZone` (the zone uses `src` only
 *   for the iframe origin). Includes the live `activityMode` on purpose: a
 *   `path` change is a soft in-app navigation (postMessage → React Router,
 *   no iframe reload), so a mode flip re-filters the grid without evicting
 *   an open document (product decision job-docs-fl7d).
 */

/**
 * Derives the embedded Documents footer state shared by JobInfo and JobInfoERP
 * from the job-docs config and the current operation.
 *
 * @param {{ partNumber?: string|number, operationId?: string|number,
 *   activityMode?: string }} [options]
 * @returns {UseJobDocsIframeResult}
 */
export function useJobDocsIframe({
  partNumber,
  operationId,
  activityMode,
} = {}) {
  const { enabled, clientUrl } = useJobDocsConfig();

  const normalizedClientUrl = normalizeClientUrl(clientUrl);

  const showSection = Boolean(
    enabled &&
      normalizedClientUrl &&
      hasValue(partNumber) &&
      hasValue(operationId)
  );

  const gridPath = useMemo(() => {
    if (!showSection) return null;
    const params = new URLSearchParams();
    params.set(JOB_DOCS_PARAM_PART_NUMBER, String(partNumber));
    params.set(JOB_DOCS_PARAM_OPERATION_ID, String(operationId));
    if (hasValue(activityMode)) {
      // The embedded header badge (normalizeJdocActivityMode) only accepts
      // lowercase modes — a capitalized 'Setup' silently drops the badge.
      params.set(
        JOB_DOCS_PARAM_ACTIVITY_MODE,
        String(activityMode).toLowerCase()
      );
    }
    // `hostNav=inplace` marks this as a host-driven, render-in-place open.
    params.set(JOB_DOCS_PARAM_HOST_NAV, JOB_DOCS_HOST_NAV_INPLACE);
    return `${DOCUMENTS_PATH}?${params.toString()}`;
  }, [showSection, partNumber, operationId, activityMode]);

  const gridUrl = useMemo(() => {
    if (!gridPath) return null;
    return `${normalizedClientUrl}${gridPath}`;
  }, [gridPath, normalizedClientUrl]);

  return { showSection, gridUrl, gridPath };
}
